/**
 * Virtual project folder tree for unversioned ("general") project files.
 *
 * Folders are NOT Firestore documents — every project shows the same default
 * tree, and a file's `folderPath` places it inside one. Files without a
 * `folderPath` (every legacy upload) land in "Unsorted", so nothing existing
 * moves or disappears.
 */

import type { DesignerStandardSubfolder, ProjectFile } from '../types';

export interface ProjectFolderDef {
  /** Stable path segment, e.g. `admin` or `admin/project-brief`. */
  path: string;
  label: string;
  children?: ProjectFolderDef[];
  /** Folder is populated from the global Designer Standards library (read-only per project). */
  designerStandards?: boolean;
  /** When set, this folder shows the matching Designer Standards subfolder. */
  designerStandardSubfolder?: DesignerStandardSubfolder;
  /** Visible and writable only for employees (see `canSeeProjectFinancials`). */
  employeeOnly?: boolean;
}

export const UNSORTED_FOLDER_PATH = '__unsorted__';
export const UNSORTED_FOLDER_LABEL = 'Unsorted';

export const DESIGNER_STANDARDS_ROOT_PATH = 'designer-standards';

/** Employee-only folder. Files live in `projectFinancialFiles`, not `projectFiles`. */
export const FINANCIALS_FOLDER_PATH = 'financials';

export const DESIGNER_STANDARD_SUBFOLDERS: {
  id: DesignerStandardSubfolder;
  label: string;
  path: string;
}[] = [
  { id: 'sops', label: 'SOPs', path: `${DESIGNER_STANDARDS_ROOT_PATH}/sops` },
  { id: 'template_2d', label: '.2d Template', path: `${DESIGNER_STANDARDS_ROOT_PATH}/template-2d` },
  { id: 'template_3d', label: '.3d Template', path: `${DESIGNER_STANDARDS_ROOT_PATH}/template-3d` },
  {
    id: 'timber_list_export',
    label: 'Timber List export',
    path: `${DESIGNER_STANDARDS_ROOT_PATH}/timber-list-export`,
  },
];

export function designerStandardSubfolderLabel(id: DesignerStandardSubfolder): string {
  return DESIGNER_STANDARD_SUBFOLDERS.find(s => s.id === id)?.label ?? id;
}

export const DEFAULT_PROJECT_FOLDERS: ProjectFolderDef[] = [
  {
    path: 'admin',
    label: 'Admin',
    children: [
      { path: 'admin/project-brief', label: 'Project Brief' },
      { path: 'admin/regulatory', label: 'Regulatory' },
    ],
  },
  {
    path: 'reference',
    label: 'Reference',
    children: [{ path: 'reference/photos', label: 'Photos' }],
  },
  {
    path: 'client-submittals',
    label: 'Client Submittals',
    children: [
      { path: 'client-submittals/2d-documentation', label: '2D Documentation' },
      { path: 'client-submittals/3d-files', label: '3D Files' },
    ],
  },
  {
    path: FINANCIALS_FOLDER_PATH,
    label: 'Financials',
    employeeOnly: true,
  },
  {
    path: DESIGNER_STANDARDS_ROOT_PATH,
    label: 'Designer Standards',
    designerStandards: true,
    children: DESIGNER_STANDARD_SUBFOLDERS.map(s => ({
      path: s.path,
      label: s.label,
      designerStandards: true,
      designerStandardSubfolder: s.id,
    })),
  },
];

/** Flat list of every folder (parents first, then children) in display order. */
export function flattenProjectFolders(tree: ProjectFolderDef[] = DEFAULT_PROJECT_FOLDERS): ProjectFolderDef[] {
  const out: ProjectFolderDef[] = [];
  const walk = (nodes: ProjectFolderDef[]) => {
    for (const n of nodes) {
      out.push(n);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(tree);
  return out;
}

const FOLDER_BY_PATH: Map<string, ProjectFolderDef> = new Map(
  flattenProjectFolders().map(f => [f.path, f]),
);

export function findProjectFolder(path: string | null | undefined): ProjectFolderDef | null {
  if (!path) return null;
  return FOLDER_BY_PATH.get(path) ?? null;
}

/** True when a path is one of the default (known) folders users may file into. */
export function isKnownProjectFolderPath(path: string | null | undefined): boolean {
  return !!path && FOLDER_BY_PATH.has(path);
}

export function isFinancialsFolderPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return path === FINANCIALS_FOLDER_PATH || path.startsWith(`${FINANCIALS_FOLDER_PATH}/`);
}

export function isEmployeeOnlyFolder(def: ProjectFolderDef | null | undefined): boolean {
  return !!def?.employeeOnly || isFinancialsFolderPath(def?.path);
}

/** Tree shown to this viewer. Financials is omitted unless `includeEmployeeOnly`. */
export function projectFoldersForViewer(includeEmployeeOnly: boolean): ProjectFolderDef[] {
  if (includeEmployeeOnly) return DEFAULT_PROJECT_FOLDERS;
  return DEFAULT_PROJECT_FOLDERS.filter(f => !f.employeeOnly).map(f => ({
    ...f,
    children: f.children?.filter(child => !child.employeeOnly),
  }));
}

/** True when a path is a folder users can drop project files into (not Designer Standards). */
export function isWritableProjectFolderPath(
  path: string | null | undefined,
  opts?: { includeEmployeeOnly?: boolean },
): boolean {
  const def = findProjectFolder(path);
  if (!def || def.designerStandards) return false;
  if (def.employeeOnly && !opts?.includeEmployeeOnly) return false;
  return true;
}

/** Writable folders (leaf + parent), for pickers. Financials only when requested. */
export function writableProjectFolders(includeEmployeeOnly = false): ProjectFolderDef[] {
  return flattenProjectFolders(projectFoldersForViewer(includeEmployeeOnly)).filter(f => !f.designerStandards);
}

/** "Admin › Project Brief" style breadcrumb label. */
export function projectFolderBreadcrumb(path: string | null | undefined): string {
  if (!path) return UNSORTED_FOLDER_LABEL;
  if (path === UNSORTED_FOLDER_PATH) return UNSORTED_FOLDER_LABEL;
  const segments = path.split('/');
  const labels: string[] = [];
  for (let i = 1; i <= segments.length; i += 1) {
    const sub = segments.slice(0, i).join('/');
    const def = FOLDER_BY_PATH.get(sub);
    labels.push(def?.label ?? segments[i - 1]);
  }
  return labels.join(' › ');
}

export function parentFolderPath(path: string): string | null {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? null : path.slice(0, idx);
}

/** Normalize a stored `folderPath` — unknown / blank values fall back to Unsorted. */
export function normalizeFolderPath(path: string | null | undefined): string {
  if (!path) return UNSORTED_FOLDER_PATH;
  const trimmed = path.trim();
  if (!trimmed) return UNSORTED_FOLDER_PATH;
  return isKnownProjectFolderPath(trimmed) ? trimmed : UNSORTED_FOLDER_PATH;
}

export interface BucketedUnversionedFiles {
  /** folderPath → files directly inside that folder (sorted newest first). */
  byFolder: Map<string, ProjectFile[]>;
  /** Files with no / unknown folder path. */
  unsorted: ProjectFile[];
  /** folderPath → recursive count (self + descendants) for badges. */
  counts: Map<string, number>;
}

/**
 * Bucket unversioned files into the virtual tree. Pure, so it can be unit-tested
 * and reused by the folder view and the upload modal.
 */
export function isEmployeeOnlyProjectFile(
  file: Pick<ProjectFile, 'employeeOnly' | 'folderPath'>,
): boolean {
  return file.employeeOnly === true || isFinancialsFolderPath(file.folderPath);
}

export function bucketUnversionedFiles(
  files: ProjectFile[],
  opts?: { includeEmployeeOnly?: boolean },
): BucketedUnversionedFiles {
  const includeEmployeeOnly = opts?.includeEmployeeOnly === true;
  const byFolder = new Map<string, ProjectFile[]>();
  const unsorted: ProjectFile[] = [];
  const counts = new Map<string, number>();

  const sorted = [...files].sort((a, b) => {
    const ta = a.uploadedAt instanceof Date ? a.uploadedAt.getTime() : 0;
    const tb = b.uploadedAt instanceof Date ? b.uploadedAt.getTime() : 0;
    return tb - ta;
  });

  for (const f of sorted) {
    if (f.streamId) continue; // defensive — versioned rows are never foldered
    if (!includeEmployeeOnly && isEmployeeOnlyProjectFile(f)) continue;
    const path = normalizeFolderPath(f.folderPath);
    if (path === UNSORTED_FOLDER_PATH) {
      unsorted.push(f);
      continue;
    }
    const list = byFolder.get(path) ?? [];
    list.push(f);
    byFolder.set(path, list);
    // Bump recursive counts up the chain.
    let cursor: string | null = path;
    while (cursor) {
      counts.set(cursor, (counts.get(cursor) ?? 0) + 1);
      cursor = parentFolderPath(cursor);
    }
  }
  if (unsorted.length) counts.set(UNSORTED_FOLDER_PATH, unsorted.length);
  return { byFolder, unsorted, counts };
}
